/**
 * Copyright (c) 2021 itemis AG - All rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Contributors:
 * 	andreas muelder - itemis AG
 *  robin herrmann - itemis ag
 *
 */
import * as joint from "@clientio/rappid";
import { dia, shapes, util } from "@clientio/rappid";
import * as ace from "ace-builds";
import { PreferenceStore } from "../services/preference-store";
import { Validation } from "./base/validation";
import { DirectEdit } from "./base/direct-edit";
import { AceFactory, DARK_MODE_THEME, LIGHT_MODE_THEME } from "../language-server/ace-factory";
import AceLanguageServer from "../language-server/ace-lsp-adapter";
import SpecificationElement from "./base/specification-element";
import NamedElement from "./base/named-element";
import { Controller } from "./base/controller";
import { difference, removeStaticAttributes } from "./base/utils";
import { LinkWarningsList } from "./LinkWarningsList";
import { LinkErrorList } from "./LinkErrorsList";
import { actionState } from "../rappid/action-state";
import DocumentationElement from "./base/documentation-element";
import { setSyntaxHighlighting } from "../language-server/ace-settings";
import { DirtyState } from "../services/dirty-state";
import RappidContext from "../rappid/rappid-context";
import { AceEditorTransition } from "../rappid/ace-editor-transition";

export const DEFAULT_CONNECTOR = "rounded";

export type NamedRouter = "normal" | "orthogonal" | "manhattan" | "metro" | "smart (obstacle-avoid)";
export type ConfiguredRouterForAllLinks = NamedRouter | "mixed";
export const POSSIBLE_ROUTING_STRATEGIES: ReadonlyArray<ConfiguredRouterForAllLinks> = [
  "normal",
  "orthogonal",
  "manhattan",
  "metro",
  "smart (obstacle-avoid)",
  "mixed",
];

export class Transition extends shapes.standard.DoubleLink implements SpecificationElement, DocumentationElement {
  errors: string[] = [];
  warnings: string[] = [];

  initialize(...args) {
    super.initialize(...args);
  }

  defaults() {
    return {
      ...super.defaults,
      type: "Transition",
      connector: { name: DEFAULT_CONNECTOR },
      attrs: {
        line: {
          connection: true,
          strokeWidth: PreferenceStore.INSTANCE.theme.model.transition.lineWidth,
          strokeLinejoin: "round",
          targetMarker: {
            type: "path",
            d: "M 8 -4 0 0 8 4 z",
          },
          stroke: null,
        },
        outline: {
          connection: true,
          stroke: "transparent",
          strokeWidth: 10,
          strokeLinejoin: "round",
        },
        specification: {
          show: true,
        },
      },
      labels: [
        // transition specification
        {
          attrs: {
            text: {
              text: "",
              strokeWidth: 1,
              fontFamily: PreferenceStore.INSTANCE.theme.model.text.fontFamily,
              cursor: "text",
              displayEmpty: true,
              lineHeight: 18,
              opacity: 0,
              fontSize: 10,
              fontWeight: "normal",
            },
            rect: {
              opacity: 0,
            },
          },
          position: {
            distance: 0.5,
            offset: -15,
          },
        },
        // transition priority
        {
          attrs: {
            root: {
              draggable: false,
              cursor: "pointer",
              opacity: 0,
            },
            label: {
              text: "",
              type: "number",
              displayEmpty: true,
              fontSize: 7,
              textVerticalAnchor: "middle",
              textAnchor: "middle",
            },
            circleBody: {
              ref: "label",
              strokeWidth: 1,
              r: 5,
            },
          },
          markup: [
            {
              tagName: "circle",
              selector: "circleBody",
            },
            {
              tagName: "text",
              selector: "label",
            },
          ],
          position: {
            distance: 6,
            args: {
              absoluteDistance: true,
            },
          },
        },
        {
          markup: [
            {
              tagName: "text",
              selector: "label",
            },
          ],
        },
        // transition documentation
        {
          attrs: {
            text: {
              text: "",
              strokeWidth: 1,
              fontFamily: PreferenceStore.INSTANCE.theme.model.text.fontFamily,
              cursor: "text",
              displayEmpty: true,
              lineHeight: 18,
              opacity: 0,
              fontSize: 10,
              fontWeight: "normal",
            },
            rect: {
              opacity: 0,
            },
          },
          position: {
            distance: 0.5,
            offset: -15,
          },
        },
      ],
    };
  }

  getName(): string {
    let source = (this.getSourceCell() as unknown as NamedElement)?.getName?.();
    source = source ? source : this.getSourceCell()?.get("type");
    let target = (this.getTargetCell() as unknown as NamedElement)?.getName?.();
    target = target ? target : this.getTargetCell()?.get("type");
    return source + " -> " + target + (this.getSpecification().length > 0 ? " (" + this.getSpecification() + ")" : "");
  }

  getSpecification(): string {
    return this.label(0).attrs?.text?.text || "";
  }

  setSpecification(spec: string): void {
    this.label(0, { attrs: { text: { text: spec } } });
  }

  getSpecificationType(): string {
    return "transition";
  }

  getShowSpecification(): boolean {
    return this.attr(["specification", "show"]);
  }

  setShowSpecification(show: boolean) {
    this.attr(["specification", "show"], show);
  }

  getDocumentation(): string {
    return this.label(3).attrs?.text?.text || "";
  }

  setDocumentation(documentation: string) {
    this.label(3, { attrs: { text: { text: documentation } } });
  }

  getDocumentationType(): string {
    return "Transition";
  }

  getLineColor(): string {
    return this.attr(["line", "stroke"]);
  }

  setLineColor(color: string | null, isCustom = true, opt: dia.Cell.Options = {}) {
    this.attr(["line", "stroke"], color, opt);
    if (color === null) {
      this.removeAttr(["line", "stroke"]);
    }
  }

  index(value?: number): number {
    // setter
    if (value !== undefined) {
      this.label(1, { attrs: { label: { text: String(value) } } });
    }
    // getter
    else {
      const index = this.label(1).attrs?.label?.text;
      return +index;
    }
  }

  hasErrors() {
    return this.errors.length > 0;
  }
  getErrorText() {
    return this.errors.join("\n");
  }
  hasWarnings() {
    return this.warnings.length > 0;
  }
  getWarningText() {
    return this.warnings.join("\n");
  }

  resetDefaults() {
    const defaultRouter = PreferenceStore.INSTANCE.linkRouter;
    if (defaultRouter) {
      this.setRouter(defaultRouter as NamedRouter);
    }
    this.connector(DEFAULT_CONNECTOR);
    this.setLineColor(null);
  }

  setRouter(newRouter: NamedRouter) {
    if (newRouter === "smart (obstacle-avoid)") {
      this.unset("router");
    } else {
      this.set("router", { name: newRouter });
    }
  }

  toJSON() {
    const result = super.toJSON();
    delete result.marker;
    result.labels = [
      {
        attrs: difference(result.labels[0].attrs, this.defaults().labels[0].attrs),
        position: difference(result.labels[0].position, this.defaults().labels[0].position),
      },
      {
        attrs: difference(result.labels[1].attrs, this.defaults().labels[1].attrs),
      },
      {
        attrs: difference(result.labels[2].attrs, this.defaults().labels[2].attrs),
      },
      {
        attrs: difference(result.labels[3].attrs, this.defaults().labels[3].attrs),
      },
    ];
    return removeStaticAttributes(result);
  }

  getLabelIndex() {
    return this.getShowSpecification() ? 0 : 3;
  }
}

export class TransitionView extends dia.LinkView implements DirectEdit, Validation {
  INIT_FLAG = 115;
  MOVE_FLAG = 6;
  RESIZE_FLAG = 28;
  ADD_FLAG = 113;

  halo: joint.ui.Halo;
  public aceEditor: AceEditorTransition | undefined;
  private revision = 0;

  constructor(opt?: dia.CellView.Options<dia.Link>) {
    super(opt);
    this.model.on("change:attrs", (model, _newValue, options) => {
      if (options.propertyPath === "attrs/specification/show") {
        const showSpecification = (model as Transition).getShowSpecification();
        this.showDocumentation(!showSpecification);
      }
    });
    this.aceEditor = new AceEditorTransition();
    this.showPriority(PreferenceStore.INSTANCE.showTransitionPriorities);
    PreferenceStore.INSTANCE.on("change:showTransitionPriorities", (evt, value) => this.showPriority(value));
  }

  showPriority(doShow: boolean): void {
    if (doShow) {
      TransitionPriorityHighlighter.add(this, "root", "transition-priority-highlighter");
    } else {
      TransitionPriorityHighlighter.remove(this, "transition-priority-highlighter");
    }
  }

  private showDocumentation(value: boolean) {
    const editor = this.aceEditor?.getEditor();
    if (editor) {
      setSyntaxHighlighting(editor, value);
    }
    this.updateTooltips();
    this.setFocus(false);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  confirmUpdate(flag: number, opt: { [key: string]: any }): number {
    const result = super.confirmUpdate(flag, opt);
    if (!this.aceEditor.isMounted()) {
      this.aceEditor.mount(this);
    }
    this.aceEditor?.resize();
    return result;
  }

  onRemove() {
    if (this.aceEditor) {
      this.aceEditor.unmount();
      this.aceEditor = undefined;
    }
    super.onRemove();
  }

  getLabelBBox(labelIndex = (this.model as Transition).getLabelIndex()) {
    const boundingRect = (this.findLabelNode(labelIndex) as SVGGraphicsElement)?.getBBox();
    return boundingRect;
  }

  setFocus(focus: boolean) {
    if (!this.aceEditor) {
      return;
    }
    this.aceEditor.setFocus(this, focus);
  }

  getHTMLContainer(): HTMLDivElement {
    const children = this.paper.el.children;
    return Array.from(children).find((child) => child.getAttribute("id") === "htmlContainer") as HTMLDivElement;
  }

  setTheme(theme: string, opt?: { override?: boolean }): this {
    const editor = this.aceEditor?.getEditor();
    editor?.setTheme(theme === "dark" ? DARK_MODE_THEME : LIGHT_MODE_THEME);
    document.documentElement.style.setProperty(
      "--transition-active-stroke",
      PreferenceStore.INSTANCE.theme.simulation.transition.lineColor
    );
    return super.setTheme(theme, opt);
  }

  getModel() {
    return this.model as Transition;
  }

  displayDiagnostics(): void {
    LinkErrorList.remove(this);
    LinkWarningsList.remove(this);
    if (this.model.attr("root/display") === "none") {
      // skip inactive views
      return;
    }
    if (this.getModel().hasErrors()) {
      LinkErrorList.add(this, "root", "errors-list", {
        attribute: "marker",
        size: 1,
        opactity: 1,
        margin: 10,
        layer: dia.Paper.Layers.FRONT,
      });
      this.model.set("marker", [this.getModel().getErrorText()]);
    } else if (this.getModel().hasWarnings() && !this.getModel().hasErrors()) {
      LinkWarningsList.add(this, "root", "warnings-list", {
        attribute: "marker",
        size: 1,
        opactity: 1,
        margin: 10,
        layer: dia.Paper.Layers.FRONT,
      });
      this.model.set("marker", [this.getModel().getWarningText()]);
    }
  }

  createHalo(): joint.ui.Halo {
    this.halo = new joint.ui.Halo({
      cellView: this,
      boxContent: "",
      handles: [],
      theme: "dark",
    });
    this.addEditLabelTool();
    this.halo.render();
    return this.halo;
  }

  private addEditLabelTool() {
    if (this.needsEditLabelTool()) {
      this.halo.addHandle(this.getEditLabelTool());
    }
  }

  private needsEditLabelTool(): boolean {
    if ((this.model as Transition).getShowSpecification()) {
      return this.getModel().getSpecification().length === 0;
    }
    return this.getModel().getDocumentation().length === 0;
  }

  private updateTooltips() {
    if (!this.halo) {
      return;
    }
    this.halo.removeHandle("editlabel");
    this.addEditLabelTool();
  }

  private getEditLabelTool(): joint.ui.Halo.Handle {
    return {
      name: "editlabel",
      position: Position.N,
      events: {
        pointerdown: () => {
          this.setFocus(true);
        },
      },
      attrs: {
        ".handle": {
          "data-tooltip-class-name": "small",
          "data-tooltip": (this.model as Transition).getShowSpecification()
            ? "Edit transition specification"
            : "Edit transition documentation",
        },
      },
    };
  }

  createLinkTools() {
    const SourceArrowhead = joint.linkTools.SourceArrowhead.extend({
      tagName: "circle",
      attributes: {
        cx: 0,
        r: 5,
        fill: PreferenceStore.INSTANCE.theme.app.primary,
        cursor: "grab",
        class: "target-arrowhead",
        "fill-opacity": 0.7,
      },
    });

    const TargetArrowhead = joint.linkTools.TargetArrowhead.extend({
      tagName: "circle",
      attributes: {
        cx: 0,
        r: 5,
        fill: PreferenceStore.INSTANCE.theme.app.primary,
        cursor: "grab",
        class: "target-arrowhead",
        "fill-opacity": 0.7,
      },
    });

    const ns = joint.linkTools;
    const toolsView = new joint.dia.ToolsView({
      name: "link-pointerdown",
      tools: [
        new ns.Vertices(),
        new SourceArrowhead(),
        new TargetArrowhead(),
        // new ns.Segments({
        //   focusOpacity: 0.5,
        //   redundancyRemoval: false,
        //   segmentLengthThreshold: 50,
        //   snapHandle: false,
        //   snapRadius: 10,
        // }),
      ],
    });
    if (!this.hasTools("link-pointerdown")) this.addTools(toolsView);
  }

  /**
   * Override to disallow moving of labels with "draggable:false"
   *
   */
  dragLabelStart(evt: dia.Event, x: number, y: number) {
    const labelNode = evt.currentTarget;
    if (labelNode.getAttribute("draggable") !== "false") {
      super.dragLabelStart(evt, x, y);
    }
  }

  /**
   * Overriden in order to not reset transition z-index after re-connecting
   */
  _afterArrowheadMove(data) {
    data.z = null;
    this.el.style.pointerEvents = data.pointerEvents;
  }
}

const Position = joint.ui.Halo.HandlePosition;
export class TransitionController extends Controller {
  constructor(view: dia.CellView, context: RappidContext) {
    super(view, context);
    view.model.on("change:router", this.onRouterChange.bind(this));
    view.model.on("change:vertices", this.onVerticesChange.bind(this));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(event: string, payload?: any): void {
    if (PreferenceStore.INSTANCE.isReadOnly) {
      return;
    }
    switch (event) {
      case "add":
        this.onAdd();
        break;
      case "remove":
        this.onRemove();
        break;
      case "cell:pointerdown":
        this.onCellPointerDown(payload?.evt);
        break;
      case "cell:pointerup":
        this.onCellPointerUp(payload?.evt);
        break;
      case "cell:validation":
        this.onCellValidation(payload?.errors, payload?.warnings);
        break;
      case "cell:pointerdblclick":
        this.onCellPointerDblClick(payload?.evt);
        break;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onVerticesChange(evt?: any, newVertices?: any, opt?: any): void {
    // If user manipulates bendpoints, prevent outsmarting the user with "smart" router => we set "normal" router.
    // BUT: We ignore vertex changes caused by movals of region/state that have embedded transitions.
    if (opt?.ui && !opt?.translateBy) {
      const transition = this.view.model as Transition;
      const currRouter = transition.get("router");
      if (!currRouter) {
        // for some reason, setting the router during onVerticesChange seems to delete all vertices!?
        // Copilot just recommended to set them again -> seems to work
        const vs = transition.get("vertices");
        transition.setRouter("normal");
        transition.set("vertices", vs);
      }
    }
  }
  onRouterChange(evt: joint.dia.Event) {
    const transition = this.view.model as Transition;
    const prevRouter = transition.previous("router");
    const currRouter = transition.get("router");
    if (!prevRouter && currRouter) {
      // No router before => routing & bendpoints likely applied by libavoid
      // JointJS routers create a mess in that case => just remove them
      transition.set("vertices", []);
    }
  }

  onCellPointerUp(evt?: joint.dia.Event) {
    // enable ace editor only when label is selected
    if (evt.target.getAttribute("class") === "ace_content") {
      return;
    }
    const tv = this.view as TransitionView;
  }
  onCellPointerDown(evt?: joint.dia.Event) {
    //Do not show the halo on middle mouse panning
    if (evt.button === 1) return;
    (this.view as TransitionView).createLinkTools();
    (this.view as TransitionView).createHalo();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCellValidation(errors?: any, warnings?: any) {
    try {
      this.context.commandManager.stopListening();
      const cell = this.view.model as Transition;
      cell.errors = Array.from<string>(new Set(errors.map((e) => e.message)));
      cell.warnings = Array.from<string>(new Set(warnings.map((w) => w.message)));
      (this.view as TransitionView).displayDiagnostics();
    } finally {
      this.context.commandManager.listen();
    }
  }

  onCellPointerDblClick(evt?: dia.Event) {
    evt.stopPropagation();
    const transitionView = this.view as TransitionView;
    transitionView.aceEditor.setFocus(transitionView, true);
  }

  onAdd() {
    const transition = this.view.model as Transition;
    transition.resetDefaults();
    this.getSourceController()?.on("link:connect");
  }

  onRemove() {
    this.getSourceController()?.on("link:disconnect");
  }

  getSourceController() {
    const transition = this.view.model as Transition;
    const sourceId = transition.attributes.source.id;
    const source = this.context.graph.getCell(sourceId);
    const sourceView = this.context.paper.findViewByModel(source);
    const sourceController = this.context.controllers.get(sourceView);
    return sourceController;
  }
}

class TransitionPriorityHighlighter extends dia.HighlighterView {
  MOUNTABLE = false;

  highlight(view: dia.CellView) {
    DirtyState.listenForChange = false;
    const label = this.findNode(view, { label: 1 });
    label.classList.add("priority-visible");
    DirtyState.listenForChange = true;
  }

  unhighlight(view: dia.CellView) {
    DirtyState.listenForChange = false;
    const label = this.findNode(view, { label: 1 });
    label.classList.remove("priority-visible");
    DirtyState.listenForChange = true;
  }
}

util.assign(shapes, {
  Transition,
  TransitionView,
  TransitionController,
});
